<template>
  <VModal :showing="showing" overflow-content container-classes="w-192" @update:showing="$emit('cancel')">
    <div class="flex items-start">
      <span class="flex-shrink-0 w-10 h-10 mr-4 rounded-full bg-gold-100 grid place-items-center">
        <ExclamationTriangleIcon class="inline h-6 text-gold-50" />
      </span>
      <div>
        <h2 class="mb-4 mr-6 text-xl">Warning - Replacement public 3D Presentation required for embeds</h2>
        <div class="font-light text-gray-700 space-y-4" data-testid="embed-deck-usage-subtitle">
          <p v-if="hasPartnerEmbeds">
            This 3D Presentation is being embedded on your website and VRIFY partner sites in the following locations
          </p>
          <p v-else>This 3D Presentation is being embedded on your website in the following locations:</p>

          <ul class="pl-5">
            <li v-for="deck in decksAffected.slice(0, 5)" :key="deck.id" class="list-disc" data-testid="embed-deck-usage-location">
              <div class="truncate">{{ displayAffectedDeckNameOrPartnerSiteName(deck) }}</div>
            </li>
            <li v-if="decksAffected.length > 5" class="mt-2 list-none">
              <em>&hellip;{{ decksAffected.length - 5 }} more&hellip;</em>
            </li>
          </ul>
          <p>
            You need to choose a public 3D Presentation to take its place so visitors don’t encounter issues viewing your VRIFY content.
          </p>
        </div>

        <VDropdown
          v-model="selectedDeck"
          placeholder="Choose a replacement public 3D Presentation"
          class="w-full mt-4"
          :options="publicDecks as unknown as Option[]"
          data-testid="embed-deck-usage-dropdown"
        >
          <template #current-single="{ item }">{{ (item as unknown as Deck).title ?? '--' }}</template>

          <template #icon>
            <span class="text-gray-400"><ChevronUpDownIcon class="w-5" /></span>
          </template>

          <template #option="{ item, selected }">
            <VRadioItem :name="(item as unknown as Deck).title" :value="item.id" :selected="selected">
              <AvConnectDeckSelectDropdownItem :item="item as unknown as Deck" :is-collapsed-state="true" />
            </VRadioItem>
          </template>
        </VDropdown>

        <div class="flex flex-row justify-end w-full mt-8 gap-4">
          <VButton data-testid="embed-deck-usage-cancel" size="sm" @click="cancel">Cancel</VButton>
          <VButton variant="dark" :disabled="!selectedDeck" data-testid="embed-deck-usage-confirm" size="sm" @click="updateAllEmbeds">
            Update all embeds
          </VButton>
        </div>
      </div>
    </div>
  </VModal>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue'
import { ref, computed } from 'vue'
import { ExclamationTriangleIcon, ChevronUpDownIcon } from '@heroicons/vue/24/outline'

import type { Option } from '@/components/VDropdown.vue'
import type { DeckWithEmbed } from '~/entities/embed'
import type { Deck } from '~/entities/deck'

const props = defineProps({
  showing: {
    type: Boolean,
    default: false,
  },
  decksAffected: {
    type: Array as PropType<DeckWithEmbed[]>,
    required: true,
  },
  publicDecks: {
    type: Array as PropType<Deck[]>,
    required: true,
  },
})

const emit = defineEmits(['confirm', 'cancel'])

const selectedDeck = ref<DeckWithEmbed | null>(null)
const hasPartnerEmbeds = computed(() => props.decksAffected.some(deck => deck.partner_id !== null))

function cancel() {
  emit('cancel')
}

function updateAllEmbeds() {
  emit('confirm', selectedDeck.value)
}

function displayAffectedDeckNameOrPartnerSiteName(embeddedDeck: DeckWithEmbed) {
  return embeddedDeck.partner_id ? `${embeddedDeck.partner_name} - ${embeddedDeck.name}` : embeddedDeck.name
}
</script>
